import { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Image, CardGroup, Spinner, Alert, Placeholder} from "react-bootstrap";
import "../assets/scss/pages/about.scss";
// import Inquiry from "../sections/Inquiry";
import Reviews from "../sections/Reviews";
import TeamMember from "../components/TeamMember";
import arrows from "../assets/images/arrows.svg";
import satisfied from "../assets/images/satisfied.svg";
import trusted from "../assets/images/trusted.svg";
import empathy from "../assets/images/empathy.svg";

const About = () =>  {
    const API = process.env.REACT_APP_API_BASE_URL;
    // const DEV_MODE = process.env.DEV_MODE;

    const [team, setTeam] = useState([]);
    const [ceo, setCeo] = useState(null);
    const [fetchFeedback, setFetchFeedback] = useState({ success: null, message: "" });
    const [isLoading, setIsLoading] = useState(true);

    // Logic for fetching team members from API
    const fetchTeam = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await fetch(`${API}/teams/`);
            if (!response.ok) {
                const errorData = await response.json();
                const errorMessage = errorData?.detail || `Error fetching team members: ${response.status} ${response.statusText}`;
                setFetchFeedback({ success: false, message: "Unable to load team members, please try again." });
                console.error(errorMessage);
            }

            const data = await response.json();

            // Sort the team members so the CEO is always first
            const allTeams = data.Teams;
            const ceoMember = allTeams.find(member => 
                member.role.toLowerCase() === "ceo" || member.role.toLowerCase() === "chief executive officer"
            );
            const otherMembers = allTeams.filter((member) => member.id !== ceoMember.id);
            const sortedTeams = ceoMember ? [ceoMember, ...otherMembers] : allTeams;

            setCeo(ceoMember);
            setTeam(sortedTeams);
            setFetchFeedback({ success: true, message: "" });
        } catch (error) {
            setFetchFeedback({ success: false, message: "An unexpected error has occured." });
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }, [API]);

    // Fetch team members
    useEffect(() => {
        fetchTeam();
    }, [fetchTeam]);

    // After a successful fetch, clear feedback after some time
    useEffect(() => {
        if (fetchFeedback.success) {
            const timer = setTimeout(() => {
                setFetchFeedback({ success: null, message: "" });
            }, 7000);

            return () => clearTimeout(timer);
        }
    }, [fetchFeedback.success]);


    return (
        <Container id="about">
            {/* Company section */}
            <Row as="section" className="about-section company d-flex justify-content-between">
                {/* CEO section */}
                <Col className="d-flex flex-column my-4" lg={4}>
                    {isLoading ? (
                        // Placeholder for CEO image
                        <Placeholder as="div" animation="glow" className="ceo-img-container aspect-ratio-box rounded-4 mb-4">
                            <Placeholder className="w-100 h-100" />
                        </Placeholder>
                    ) : (
                        // Corrected image container structure
                        <div className="ceo-img-container aspect-ratio-box rounded-4 mb-4">
                            {ceo && <Image src={ceo?.image} className="rounded-4" alt={ceo?.name} />}
                        </div>
                    )}

                    <h5 className="text-primary mt-3 fw-semibold">Precious Afinni</h5>
                    <p className="d-inline-flex flex-row fw-semibold fs-6">
                        Chief Operations Officer
                        <img className="ms-3" alt="arrows" src={arrows} />
                    </p>
                </Col>
                {/* Company summary */}
                <Col lg={7} className="align-self-end d-flex flex-column gap-4 my-4">
                    <h3 className="text-primary fw-bold p-0 d-none d-lg-block">AFINNEY REALTY</h3>
                    <p className="lh-lg">Founded by <strong className="fw-semibold">Precious Afinni</strong> a seasoned real estate professional with over <strong className="fw-semibold">five years</strong> of industry experience, Afinney Realty Ltd specializes in the sale of land and houses, partnering with reputable developers to bring you high-value real estate opportunities. We have quickly gained a reputation for prioritizing client satisfaction, trust, and transparency.</p>
                    <p className="lh-lg"><strong className="fw-semibold">Our mission</strong> is to simplify the property buying process through expert consultation, personalized property tours, and seamless transactions. With a deep understanding of the market and our clients' needs, we guide you in selecting the right properties that align with your goals.
                    Whether you're making your first property purchase or adding to your portfolio, we look forward to walking you through your property ownership and investment journey.</p>
                </Col>
            </Row>

            {/* Core values section */}
            <Row as="section" className="about-section core-values d-flex flex-row justify-content-between">
                {/* Section heading */}
                <Col className="d-flex flex-column justify-content-center gap-2" lg={4}>
                    <h2 className="text-primary text-center mb-4 lh-base"><b>OUR</b> CORE <br />VALUES</h2>
                    <p className="text-center d-inline-block mx-4 mx-sm-5">At Afinney Realty, we pride ourselves in providing unparalleled service and exceeding expectations for every client.</p>
                </Col>
                {/* Value cards */}
                <Col lg={8}>
                    <Row xs={1} sm={2} md={2} className="mx-auto">
                        <Col className="value d-flex flex-column justify-content-center gap-3 rounded-3 border-start border-end border-primary mx-auto mb-5">
                            <Image className="align-self-center" src={trusted} alt="trusted" />
                            <h6 className="text-primary text-center fw-semibold">1. Transparency</h6>
                        </Col>
                        <Col className="value d-flex flex-column justify-content-center gap-3 rounded-3 border-start border-end border-primary mx-auto mb-5">
                            <Image className="align-self-center" src={satisfied} alt="trusted" />
                            <h6 className="text-primary text-center fw-semibold">2. Client Satisfaction</h6>
                        </Col>
                        <Col className="value d-flex flex-column justify-content-center gap-3 rounded-3 border-start border-end border-primary mx-auto mb-5">
                            <Image className="align-self-center" src={empathy} alt="trusted" />
                            <h6 className="text-primary text-center fw-semibold">3. Empathy</h6>
                        </Col>
                    </Row>
                </Col>
            </Row>

            {/* Team section */}
            <Row as="section" id="team" className="about-section team d-flex gap-2" aria-labelledby="team-heading">
                <h2 id="team-heading" className="text-primary text-center"><b>MEET</b> OUR TEAM</h2>
                <p className="text-center">Meet the amazing people behind the brand.</p>
                {/* Fetch Team Alert */}
                { fetchFeedback.success === false && // Display this alert only when fetchTeam fails
                    <Alert variant="danger" className="text-center">{ fetchFeedback.message }</Alert>
                }
                {isLoading ? (
                    <div className="text-center mt-5">
                        <Spinner animation="border" variant="primary" className="mx-auto" />
                        <p className="mt-2">Loading team members...</p>
                    </div>
                ) : (
                    <CardGroup as={Row} className="my-4 mx-auto">
                        {team?.length > 0
                            ? (team.map((member) => (
                                <Col lg={4} md={6} sm={12} key={ member.id } className="my-3">
                                    <TeamMember data={ member } />
                                </Col>)
                            ))
                            :
                            (<p className="text-center">No team members yet.</p>)
                        }
                    </CardGroup>
                )}
            </Row>

            {/* Inquiry section*/}
            {/* <Inquiry contactCardType="icons"/> */}

            {/* Reviews section */}
            <Reviews />
        </Container>
    );
};

export default About;